import moment from "moment";
import React from "react";
import { getUpdateGroundTransportInstruction, GroundTransportFormState, mapGroundTransportToState } from "shared/utils/fieldsHelper/groundFields";
import { hasDriverInfo } from "shared/utils/groundTransportUtils";

export const useGroundTransportDetail = (
  groundTransport: server.dto.GroundTransport,
  travellers: server.dto.ContactSmallV2[],
  onArtistChanged: (newArtist: server.dto.ArtistSmall) => void,
  onDateChanged: (newDate: server.dto.AppointmentDateTime) => void,
  onStatusChanged: () => void,
) => {
  const [state, setState] = React.useState<GroundTransportFormState>(mapGroundTransportToState(groundTransport, travellers, serverReference));

  React.useEffect(() => {
    setState(mapGroundTransportToState(groundTransport, travellers, serverReference));
  }, [groundTransport, travellers]);

  const hasDriver = hasDriverInfo(state.driver);
  const hasMap = Boolean(state.startPlace.placeIdentifier || state.endPlace.placeIdentifier);

  const onValueChanged = (key: string, value: any) => {
    if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "travellers") {
      setState({ ...state, travellers: value });
    } else if (key === "status") {
      setState({ ...state, phase: value });
    } else if (key === "transportType") {
      setState({ ...state, type: value });
    } else if (key === "notes") {
      setState({ ...state, notes: value });
    } else if (key === "driver") {
      setState({ ...state, driver: value });
    } else if (key === "typeFrom") {
      setState({
        ...state,
        startPickupLocationType: value.pickupLocationType,
        startPickupLocationOther: value.pickupLocationOther,
      });
    } else if (key === "typeTo") {
      setState({
        ...state,
        endPickupLocationType: value.pickupLocationType,
        endPickupLocationOther: value.pickupLocationOther,
      });
    } else if (key === "addressFrom") {
      setState({ ...state, startPlace: value });
    } else if (key === "addressTo") {
      setState({ ...state, endPlace: value });
    } else if (key === "date") {
      setState({ ...state, date: value });
    } else if (key === "durationInMinutes") {
      setState({ ...state, durationInMinutes: value });
    }
  }

  const onSuccessfulStatusUpdate = () => {
    onStatusChanged();
  }

  const onSuccessfulDateUpdate = async () => {
    const newData = await window.CoreApi.GroundTransport.GetSidePanelData(groundTransport.groundTransportId);
    if (newData.data.hasErrors) {
      return;
    }
    onDateChanged(newData.data.data.groundTransport.dateTime);
    setState({
      ...state,
      appointmentDateTime: newData.data.data.groundTransport.dateTime,
      date: {
        departArrive: newData.data.data.groundTransport.departOrArrive,
        dateTime: newData.data.data.groundTransport.departOrArrive === "departAt"
          ? moment.utc(newData.data.data.groundTransport.dateTime.startDisplay)
          : moment.utc(newData.data.data.groundTransport.dateTime.endDisplay),
        inTheNightOf: newData.data.data.groundTransport.dateTime.mode === "hasTimeStartsInNight",
      },
      durationInMinutes: newData.data.data.groundTransport.durationInMinutes,
    });
  }

  const onSuccessfulAddressUpdate = async () => {
    const newData = await window.CoreApi.GroundTransport.GetSidePanelData(groundTransport.groundTransportId);
    if (newData.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      startPlace: {
        googlePlaceId: 0, // This will be updated in the future to work like the train
        placeIdentifier: newData.data.data.groundTransport.startGooglePlaceIdentifier,
        name: newData.data.data.groundTransport.startGooglePlaceName,
        address: newData.data.data.groundTransport.startGooglePlaceAddress,
        geo: newData.data.data.groundTransport.startGooglePlaceGeo,
      },
      endPlace: {
        googlePlaceId: 0, // This will be updated in the future to work like the train
        placeIdentifier: newData.data.data.groundTransport.endGooglePlaceIdentifier,
        name: newData.data.data.groundTransport.endGooglePlaceName,
        address: newData.data.data.groundTransport.endGooglePlaceAddress,
        geo: newData.data.data.groundTransport.endGooglePlaceGeo,
      },
      durationInMinutes: newData.data.data.groundTransport.durationInMinutes,
      directions: newData.data.data.groundTransport.directions,
    });
  }

  const onSuccessfulArtistUpdate = async (newArtist: server.dto.ArtistSmall) => {
    const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
      companyId: newArtist.artistId,
      defaultSelect: true,
      isActive: true,
      page: 1,
      perPage: 1000,
    });

    if (result.data.hasErrors) {
      return;
    }

    const newTravellers = result.data.data.map(c => ({
      contactId: c.contact.contactId,
      culture: c.contact.culture,
      gender: c.contact.gender,
      isValidated: c.contact.isValidated,
      name: c.contact.name,
      position: c.contact.position,
      profileImageUrl: c.contact.profileImageUrl,
    }))

    const updateDetailsResults = await window.CoreApi.GroundTransport.Update({
      ...getUpdateGroundTransportInstruction({
        ...state,
        artist: newArtist,
        travellers: newTravellers,
      }),
    });

    if (updateDetailsResults.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      artist: newArtist,
      travellers: newTravellers,
    });

    onArtistChanged(newArtist);
  }

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    if (key === "status") {
      onSuccessfulStatusUpdate();
      return;
    }

    if (key === "addressFrom" || key === "addressTo") {
      await onSuccessfulAddressUpdate();
      return;
    }

    if (key === "artist") {
      await onSuccessfulArtistUpdate(newValue);
      return;
    }

    if (key === "date" || key === "durationInMinutes") {
      await onSuccessfulDateUpdate();
      return;
    }
  }

  return {
    state,
    hasDriver,
    hasMap,
    onValueChanged,
    onSuccessfulUpdate,
    getUpdateGroundTransportInstruction,
  }
}